interface MerchantBase {
	name: string;
	website: string;
}

interface Merchant extends MerchantBase {
	id: string;
	settings: string;
	webhookSettings: WebhookSettings;
	supportedPaymentMethods: PaymentMethod[];
}

interface WebhookSettings {
	secret?: string;
	url: string;
}

const BLOCKCHAIN = ["ETH", "TRON", "MATIC", "BSC", "BTC"] as const;
type Blockchain = (typeof BLOCKCHAIN)[number];

const BLOCKCHAIN_TICKER = [
	"ETH",
	"ETH_USDT",
	"ETH_USDC",
	"MATIC",
	"MATIC_USDT",
	"MATIC_USDC",
	"TRON",
	"TRON_USDT",
	"BNB",
	"BSC_USDT",
	"BSC_BUSD",
	"BTC",
] as const;

type BlockchainTicker = (typeof BLOCKCHAIN_TICKER)[number];

interface PaymentMethod {
	blockchain: Blockchain;
	blockchainName: string;
	displayName: string;
	enabled: boolean;
	name: string;
	ticker: BlockchainTicker;
}

interface MerchantToken {
	name: string;
	token: string;
	id: string;
	createdAt: string;
}

interface User {
	email: string;
	name: string;
	profileImageUrl: string;
	uuid: string;
}

interface AuthProvider {
	name: string;
}

interface UserCreateForm {
	email: string;
	password: string;
}

interface MerchantBalance {
	amount: string;
	usdAmount: string;
	blockchain: string;
	blockchainName: string;
	currency: string;
	id: string;
	isTest: boolean;
	minimalWithdrawalAmountUSD: string;
	name: string;
	ticker: BlockchainTicker;
}

// Request type for creating a withdrawal (used by the form)
interface WithdrawalCreateRequest {
	addressId: string;
	amount: string;
	balanceId: string;
	deductFeeFromAmount?: boolean;
}

// Full withdrawal object returned by the API
interface Withdrawal {
	uuid: string;
	merchantId: string;
	balanceId: string;
	recipientAddress: string;
	amount: string;
	serviceFee: string;
	status: PaymentStatus;
	transactionHash?: string;
	explorerLink?: string;
	transactionStatus?: string;
	transactionNetworkFee?: string;
	description?: string;
	failureReason?: string;
	createdAt: string;
	updatedAt?: string;
	statusRaw?: string;
}

const CURRENCY = ["USD", "EUR"] as const;
type Currency = (typeof CURRENCY)[number];
type CurrencyWithFiat = Currency | BlockchainTicker;

const CURRENCY_SYMBOL: Record<CurrencyWithFiat, string> = {
	USD: "$",
	EUR: "€",
	ETH: "",
	ETH_USDT: "",
	ETH_USDC: "",
	MATIC: "",
	MATIC_USDT: "",
	MATIC_USDC: "",
	TRON: "",
	TRON_USDT: "",
	BNB: "",
	BSC_USDT: "",
	BSC_BUSD: "",
	BTC: "",
};

type PaymentType = "payment" | "withdrawal";

type PaymentStatus =
	| "pending"
	| "locked"
	| "inProgress"
	| "success"
	| "failed"
	| "aml_rejected";

interface ServiceFee {
	blockchain: string;
	calculatedAt: string;
	currency: string;
	currencyFee: string;
	isTest: boolean;
	usdFee: string;
}

interface PaymentParams {
	orderId: string;
	price: number;
	currency: Currency;
	redirectUrl?: string;
	isTest?: boolean;
	description?: string;
}

interface AdditionalPaymentInfo {
	customerEmail: string;
	selectedCurrency: string;
	serviceFee: string;
}

interface AdditionalWithdrawalInfo {
	addressId: string;
	balanceId: string;
	explorerLink: string | null;
	serviceFee: string;
	transactionHash: string | null;
}

interface AdditionalInfo {
	payment?: AdditionalPaymentInfo;
	withdrawal?: AdditionalWithdrawalInfo;
}

interface Payment {
	additionalInfo?: AdditionalInfo;
	id: string;
	orderId?: string;
	type: PaymentType;
	status: PaymentStatus;
	createdAt: string;
	currency: CurrencyWithFiat;
	price: string;
	redirectUrl?: string;
	paymentUrl?: string;
	description?: string;
	isTest: boolean;
}

interface MerchantAddress {
	address: string;
	blockchain: string;
	blockchainName: string;
	id: string;
	name: string;
}

interface MerchantAddressParams {
	address: string;
	blockchain: string;
	name: string;
}

interface ListPaymentParams {
	limit?: number;
	offset?: number;
	search?: string;
	status?: PaymentStatus;
}

type PaymentLinkAction = "redirect" | "showMessage";

interface PaymentLinkParams {
	id?: string;
	currency: Currency;
	description?: string;
	name: string;
	price: number;
	redirectUrl?: string;
	successAction: PaymentLinkAction;
	successMessage?: string;
}

interface PaymentLink {
	createdAt: string;
	currency: Currency;
	description?: string;
	id: string;
	name: string;
	price: number;
	redirectUrl?: string;
	successAction: PaymentLinkAction;
	successMessage?: string;
	url: string;
}

interface PaymentsPagination {
	results: Payment[];
	totalCount: number;
	limit: number;
	offset: number;
}

interface ErrorResponseItem {
	field: string;
	message: string;
}

interface ErrorResponse {
	errors: ErrorResponseItem[];
	message: string;
	status: string;
}

interface SupportMessage {
	topic: string;
	message: string;
}

interface CustomerPayment {
	createdAt: string;
	currency: Currency;
	id: string;
	price: string;
	status: PaymentStatus;
}

interface CustomerDetails {
	payments: CustomerPayment[];
	successfulPayments: number;
}

interface Customer {
	createdAt: string;
	details?: CustomerDetails;
	email: string;
	id: string;
}

interface CustomerPagination {
	cursor: string;
	limit: number;
	results: Customer[];
}

interface ListCustomersParams {
	limit?: number;
	cursor?: string;
	reverseOrder?: boolean;
}

interface ConvertParams {
	from: CurrencyWithFiat;
	amount: string;
	to: CurrencyWithFiat;
}

interface ConvertResult {
	convertedAmount: string;
	exchangeRate: number;
	from: CurrencyWithFiat;
	to: CurrencyWithFiat;
}

type OrderStatus =
	| "pending"
	| "paid"
	| "partially_paid"
	| "expired"
	| "cancelled"
	| "aml_rejected";

interface OrderParams {
	merchantOrderId: string;
	currency: Currency;
	price: number;
	cryptoCurrency: BlockchainTicker;
	description?: string;
	// Optional per-order underpayment tolerance as a percent of the order price.
	// Omit to use the merchant's account-level configuration. 0 requires exact payment.
	paymentTolerancePercent?: number;
}

interface Order {
	id: string;
	createdAt: string;
	expiresAt: string;
	merchantOrderId: string;
	currency: Currency;
	price: string;
	cryptoCurrency: BlockchainTicker;
	cryptoBlockchain: string;
	cryptoAmount: string;
	paymentAddress: string;
	status: OrderStatus;
	description?: string;
	receivedAmount?: string;
	// The merchant's per-order tolerance input, echoed back. null means "no override".
	paymentTolerancePercent: number | null;
	// The resolved USD tolerance at emission time — always present.
	paymentToleranceUsd: string;
}

interface OrdersPagination {
	results: Order[];
	totalCount: number;
	limit: number;
	offset: number;
}

interface ListOrdersParams {
	status?: OrderStatus;
	search?: string;
	currency?: string;
	cryptoCurrency?: string;
	createdFrom?: string;
	createdTo?: string;
	limit?: number;
	offset?: number;
}

interface PaymentLinksPagination {
	results: PaymentLink[];
	totalCount: number;
	limit: number;
	offset: number;
}

interface ListPaymentLinksParams {
	search?: string;
	currency?: string;
	createdFrom?: string;
	createdTo?: string;
	limit?: number;
	offset?: number;
}

interface WithdrawalsPagination {
	data: Withdrawal[];
	totalCount: number;
	limit: number;
	offset: number;
}

interface ListWithdrawalsParams {
	status?: string;
	search?: string;
	currency?: string;
	createdFrom?: string;
	createdTo?: string;
	limit?: number;
	offset?: number;
}

// AML Types
type AMLTransactionStatus = "on_hold" | "in_progress" | "cancelled";

type AMLRiskLevel = "low" | "medium" | "high" | "critical";

interface AMLCheck {
	id: string;
	transactionId: number;
	provider: string;
	riskScore: number;
	riskLevel: AMLRiskLevel;
	checkData: Record<string, unknown>;
	createdAt: string;
}

interface AMLTransaction {
	id: number;
	merchantId: number;
	type: string;
	currency: string;
	amount: string;
	senderAddress: string | null;
	recipientAddress: string;
	status: AMLTransactionStatus;
	amlCheck: AMLCheck | null;
	metaData: Record<string, string> | null;
	createdAt: string;
	updatedAt: string;
}

interface AMLTransactionsPagination {
	transactions: AMLTransaction[];
	total: number;
}

interface AMLApproveRequest {
	reason: string;
}

interface AMLRejectRequest {
	reason: string;
}

interface ListAMLTransactionsParams {
	status?: AMLTransactionStatus;
	riskLevel?: AMLRiskLevel;
	limit?: number;
	offset?: number;
}

interface MerchantNotification {
	channel: string;
	enabled: boolean;
	config: Record<string, string>;
	events: Record<string, boolean>;
}

interface MerchantNotificationUpsertRequest {
	enabled: boolean;
	config?: Record<string, string>;
	events?: Record<string, boolean>;
}

interface NotificationTestResult {
	ok: boolean;
	error?: string;
}

export type {
	MerchantBase,
	Merchant,
	MerchantToken,
	User,
	PaymentMethod,
	BlockchainTicker,
	PaymentParams,
	Payment,
	ListPaymentParams,
	PaymentsPagination,
	Currency,
	ErrorResponse,
	MerchantBalance,
	Withdrawal,
	WithdrawalCreateRequest,
	MerchantAddress,
	MerchantAddressParams,
	WebhookSettings,
	ServiceFee,
	SupportMessage,
	Customer,
	CustomerPagination,
	ListCustomersParams,
	PaymentStatus,
	ConvertParams,
	ConvertResult,
	CustomerPayment,
	PaymentLinkParams,
	PaymentLink,
	PaymentLinkAction,
	PaymentLinksPagination,
	ListPaymentLinksParams,
	WithdrawalsPagination,
	ListWithdrawalsParams,
	UserCreateForm,
	AuthProvider,
	OrderParams,
	Order,
	OrdersPagination,
	OrderStatus,
	ListOrdersParams,
	AMLTransaction,
	AMLTransactionsPagination,
	AMLApproveRequest,
	AMLRejectRequest,
	AMLCheck,
	AMLTransactionStatus,
	AMLRiskLevel,
	ListAMLTransactionsParams,
	MerchantNotification,
	MerchantNotificationUpsertRequest,
	NotificationTestResult,
};
export { BLOCKCHAIN, BLOCKCHAIN_TICKER, CURRENCY, CURRENCY_SYMBOL };
